import {
  Component,
  OnInit,
  Renderer2,
} from '@angular/core';
import { ServicesStartupService } from './services/services-startup/services-startup.service';
import { Capacitor } from '@capacitor/core';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { NavController, Platform } from '@ionic/angular';
import { EventService } from './services/events/event.service';
import { environment } from 'src/environments/environment';
import { register } from 'swiper/element/bundle';
import { SecureStorageKey } from './services/storage/models/secure-storage-key.enum';
import { SecureStorageService } from './services/storage/secure-storage.service';
import { LoadingService } from './services/loading-service/loading.service';
import { TranslateService } from '@ngx-translate/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { OrganizationTokenModel } from './api/proxy/organization/organization-models';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { EnvironmentConfigService, ILocalAppEnvironment } from './services/environment-config-service/environment-config.service';
import { Dialog } from '@capacitor/dialog';


register();

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit {
  // public showMenu = true;
  busyLoading = false;
  isReady = false;
  chatAllowed = false;
  chatRouteAllowed = false;
  appDisabled = false;
  updateAvailable = false;
  allowFullScreen = false;

  constructor(
    public servicesStartupService: ServicesStartupService,
    public navCtrl: NavController,
    private renderer: Renderer2,
    private _translate: TranslateService,
    private platform: Platform,
    private router: Router,
    private _EnvironmentConfigService: EnvironmentConfigService,

  ) {
    this.platform.ready().then((value: string) => {
      setTimeout(async () => {
        try {
          await this.initializeApp(value);
        } catch (err) {
          console.error('App initialization failed', err);
          // optionally set fallback state or show an error overlay
        } finally {
          try {
            await SplashScreen.hide();
          } catch {

          }


        }
      }, 500);
    });


  }

  async initializeApp(value: string) {
    await App.removeAllListeners();

    await App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      setTimeout(async () => {
        if (event.url?.startsWith('msauth')) {
          const url = new URL(event.url);

          const code = url.searchParams.get('code');
          const error = url.searchParams.get('error');
          const state = url.searchParams.get('state');
          const errorDescription = url.searchParams.get('error_description');

          // Build query params string manually
          const query = new URLSearchParams();
          if (code) query.append('code', code);
          if (error) query.append('error', error);
          if (errorDescription) query.append('error_description', errorDescription);
          if (state) query.append('state', state);

          const fullUrl = `/login-callback?${query.toString()}`;



          await this.navCtrl.navigateRoot(fullUrl);
          return;
        }

        const slug = event.url.split(".app").pop();
        if (slug) {
          try {
            let path = slug.toLowerCase();
            if (path.includes(environment.deepLinkURLBase)) {
              path = path.replace(environment.deepLinkURLBase, '');
              if (path.startsWith('/'))
                path = path.replace(/^./, "");
            }

            if (path.includes('wearable-connected')) {
              path = '/wearable-connected';
            }

            await this.navigateToDeepLink(path);
          } catch (e) {
            console.error(e);
          }
        }
      }, 500);
    });

    await App.addListener('appStateChange', async (state) => {
      if (state.isActive) {
        setTimeout(async () => {
          await this.appResumed();
        }, 500);
      } else {
        // Your pause logic
        await this.appPaused();
      }
    });

    LoadingService.init();
    //ensure translations
    await this._translate.setDefaultLang(environment.defaultLanguage);

    EventService.loadingEvent.subscribe(async (loading: number) => {
      this.busyLoading = loading > 0;
    });

    EventService.environmentLoaded.subscribe(async (config: ILocalAppEnvironment) => {
      this.appDisabled = config && config.versionDisabled == true;
      this.updateAvailable = config && config.updateRequired == true;
    });

    EventService.organizationConfigLoaded.subscribe(async (v: OrganizationTokenModel) => {
      this.chatAllowed = v && v.aiChatEnabled === true;
      if (this.chatAllowed) {
        await this.checkChatRoute();
      }
    });

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const url = event.urlAfterRedirects;
        try {
          // Now decide whether chat is allowed on this route      
          this.allowFullScreen = this.isFullScreenRoute(url);
          this.chatRouteAllowed = this.isChatRouteAllowed(url);
        } catch {
          this.chatRouteAllowed = false;
        }
      });

    await this.servicesStartupService.initServices();

    this.isReady = true;

    const initialUrl = this.router.url;
    this.allowFullScreen = this.isFullScreenRoute(initialUrl);
    this.chatRouteAllowed = this.isChatRouteAllowed(initialUrl);

    if (!this.isWebViewCompatible()) {
      this.isReady = false;

      setTimeout(async () => {
        await Dialog.alert({
          title: 'WebView Update Required',
          message: 'Your device is using an outdated WebView that may cause errors. Please update Chrome or Android System WebView in the Play Store.',
        });
        await App.exitApp();
      }, 1000);
    }


  }

  isWebViewCompatible(): boolean {
    if (Capacitor.getPlatform() != "android")
      return true;



    const match = navigator.userAgent.match(/Chrome\/(\d+)/);
    if (!match) return true; // Not a Chrome-based environment

    const version = parseInt(match[1], 10);
    if (version >= 93) {
      return true;
    } else {
      return false;
    }
  }

  async checkChatRoute() {
    const url = this.router.url;
    this.chatRouteAllowed = this.isChatRouteAllowed(url);
  }

  async appResumed() {
    try {
      await this.servicesStartupService.resumeApp();
      EventService.appResumed.emit();

    } catch (error) {
      console.error('Error on app resume:', error);
      EventService.appResumed.emit();
    }
  }

  async appPaused() {
    EventService.appPaused.emit();
  }


  async ngOnInit(): Promise<void> {
    setTimeout(() => {
      if (!Capacitor.isNativePlatform()) {
        this.renderer.addClass(document.body, 'web-platform');
      }
    }, 500);
  }

  async navigateToDeepLink(path: string): Promise<void> {
    const linkPath = {
      path: path
    }
    const pathJSON = JSON.stringify(linkPath);
    await SecureStorageService.set(SecureStorageKey.DeeplinkPath, pathJSON);
    EventService.appDeeplinkEvent.emit(path);
  }

  private inAIRedirect = false;
  public async aiChatClicked(): Promise<void> {
    if (this.inAIRedirect)
      return;

    this.inAIRedirect = true;
    try {
      const currentUrl = this.router.url;
      const encodedUrl = encodeURIComponent(currentUrl);
      await EventService.onAIChatStart.emit(currentUrl);
      await this.navCtrl.navigateForward('home/mediaChatbot?src=' + encodedUrl);
    } finally {
      this.inAIRedirect = false;
    }
  }


  private isChatRouteAllowed(url: string): boolean {
    // Define routes where chat should be allowed
    const allowedRoutes = [
      '/home/biometric-detail',
      '/home/wearable-detail',
      '/home/healthScore', // add as needed
    ];

    // Exact match
    return allowedRoutes.some(route => url.startsWith(route));
  }

  private isFullScreenRoute(url: string): boolean {
    // Define routes where chat should be allowed
    const allowedRoutes = [
      '/home/powerbi',
      '/v2/welcome/intro-page'
    ];

    // Exact match
    return allowedRoutes.some(route => url.startsWith(route));
  }


  async updateApp(): Promise<void> {
    await this._EnvironmentConfigService.OpenAppStore();
  }


}
